/**
 * The handle between the chain and the book.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A PROPORTION, NOT A PIXEL COUNT
 *
 * The split is stored as a PERCENTAGE of the workspace and clamped to
 * 30–60. A pixel width would be right on the monitor it was dragged on and
 * wrong on every other one; a proportion survives a laptop, a 27-inch
 * screen and a window the user half-sizes.
 *
 * THE BOUNDS ARE THE POINT. Below 30% the book's seven-column tables stop
 * being readable; above 60% the ladder drops below `--tr-chain-min` and
 * starts scrolling sideways. So the handle simply stops, rather than letting
 * a drag produce a layout neither half can use.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DRAG DOES NOT GO THROUGH REACT, AND THAT IS THE WHOLE PERFORMANCE STORY
 *
 * The first version called `onPct` on every `pointermove`. That is a state
 * change on the Terminal, so every frame of a drag re-rendered the ladder,
 * the drawer, every trade card and every leg row — a hundred components to
 * move one divider. It felt like it was dragging through treacle, because
 * it was.
 *
 * Now a move writes two CSS custom properties straight onto the workspace
 * node, inside one `requestAnimationFrame`, and React hears nothing until
 * the pointer comes up. The browser then has only the layout to do, which is
 * the work that genuinely cannot be avoided.
 *
 * REACT MUST NOT OWN THE VARIABLE EITHER. If the Terminal rendered
 * `style={{ '--tr-drawer-w': ... }}`, any unrelated re-render mid-drag — the
 * one-second clock, a tick, an order event — would re-apply the committed
 * value and snap the divider back under the cursor. So this component is the
 * only writer, and the Terminal never touches it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE VARIABLE, AND NOTHING MEASURES ANYTHING
 *
 * An earlier version also published the width in pixels, so the drawer's
 * body could be pinned to it and skip a re-layout when the rail collapsed.
 * It cost two layouts per drawer toggle and saved two — and keeping it true
 * needed a `ResizeObserver`, which fired on EVERY FRAME of the app
 * sidebar's own open animation, measured the workspace and wrote the
 * variable, adding a second layout pass per frame to an animation that was
 * already the heaviest thing on the screen.
 *
 * So it is gone. The body is sized at `100%` of the drawer again and
 * re-laid-out by the same layout pass that resized the drawer, rather than
 * by a second one we asked for. Nothing here reads geometry at all now: the
 * only measurement in the whole component is the workspace width taken once
 * at pointer-down, which is what converts a pixel of travel into a percent.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { GripVertical } from 'lucide-react'
import { cn } from '@/lib/utils'

export const DRAWER_MIN_PCT = 30

/**
 * Drag past this and the chain CLOSES rather than being squeezed.
 *
 * The ladder has a minimum readable width (`--tr-chain-min`). Past about
 * seventy percent it is no longer a chain, it is a column of clipped
 * numbers with a horizontal scrollbar — present, unusable, and still taking
 * the space. A trader dragging that far has said what they want: the book,
 * not the chain.
 *
 * So the drag SNAPS: under 70 the split is continuous, at 70 the chain
 * disappears and the book takes the whole workspace. Dragging back the
 * other way restores it at the width the pointer is actually over, so the
 * gesture is reversible without a button to find.
 */
export const DRAWER_FULL_PCT = 70
export const DRAWER_MAX_PCT = 100
export const DRAWER_DEFAULT_PCT = 50

/** True when the book has the workspace to itself. */
export const isChainHidden = (pct: number): boolean => pct >= DRAWER_MAX_PCT

const STORE_KEY = 'apna.terminal.drawerPct'

/**
 * Into range, and never NaN.
 *
 * `Math.min`/`Math.max` propagate NaN rather than clamping it, so an
 * unparseable stored value or a measurement taken against a zero-width
 * element would have produced `width: NaN%` and a drawer of no width at
 * all. A non-finite input is a bug upstream; the even split is the least
 * surprising thing to show while it is found.
 */
export const clampPct = (v: number): number => {
  if (!Number.isFinite(v)) return DRAWER_DEFAULT_PCT
  // Past the threshold there is no intermediate state: the chain is closed
  // and the book is full width. Anything between 70 and 100 would be a
  // chain too narrow to read occupying space the book could use.
  if (v >= DRAWER_FULL_PCT) return DRAWER_MAX_PCT
  return Math.max(DRAWER_MIN_PCT, Math.round(v * 10) / 10)
}

/**
 * The remembered split.
 *
 * Every read and write is guarded: `localStorage` throws in a private window
 * and comes back empty after site data is cleared, and a layout preference
 * is not worth taking the terminal down for.
 */
export function useDrawerWidth(): { pct: number; setPct: (v: number) => void } {
  const [pct, setStored] = useState<number>(() => {
    try {
      const raw = window.localStorage.getItem(STORE_KEY)
      return raw === null ? DRAWER_DEFAULT_PCT : clampPct(Number(raw))
    } catch {
      return DRAWER_DEFAULT_PCT
    }
  })

  const setPct = useCallback((v: number) => {
    const next = clampPct(v)
    setStored(next)
    try {
      window.localStorage.setItem(STORE_KEY, String(next))
    } catch {
      // A preference that cannot be remembered is still one that works for
      // this session.
    }
  }, [])

  return { pct, setPct }
}

export interface DrawerResizerProps {
  /** False in rail mode: the handle becomes a plain gap. */
  active: boolean
  /** The COMMITTED split. During a drag the DOM runs ahead of this. */
  pct: number
  /** Called once, on release — never per frame. */
  onPct: (v: number) => void
}

export function DrawerResizer({ active, pct, onPct }: DrawerResizerProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)

  /** The split the DOM is currently showing. Ahead of `pct` while dragging. */
  const live = useRef(pct)
  /** Where the drag began, so the divider tracks the cursor without drift. */
  const from = useRef<{ x: number; pct: number; width: number } | null>(null)
  const frame = useRef(0)
  const queued = useRef(pct)

  /**
   * Write the split to the workspace. One property, no measurement.
   *
   * Writing a custom property and an ARIA attribute dirties layout once and
   * reads nothing back, so a frame costs exactly the layout the resize
   * genuinely needs. The moment anything here reads geometry — a
   * `getBoundingClientRect` after a style write — the browser has to lay the
   * page out on the spot to answer it, and the frame pays twice.
   */
  const apply = useCallback((next: number) => {
    const ws = ref.current?.parentElement
    if (!ws) return
    live.current = next
    ws.style.setProperty('--tr-drawer-w', `${next}%`)
    /*
     * A data attribute rather than a React class: this runs mid-drag, on
     * the DOM, for the same reason the width does — re-rendering the whole
     * workspace to hide a pane would drop frames in the middle of the
     * gesture that hides it.
     *
     * `active` IS LOAD-BEARING, and leaving it out shipped a blank screen.
     * The drawer can be collapsed to its rail while `pct` is still 100 —
     * the remembered split outlives the collapse, which is the point of
     * remembering it. Hiding the chain on `pct` alone then left the chain
     * display:none with no drawer either: an empty workspace and no control
     * on it to recover from.
     *
     * The chain is hidden only while something is actually covering it.
     */
    if (active && isChainHidden(next)) ws.setAttribute('data-chain-hidden', '1')
    else ws.removeAttribute('data-chain-hidden')
    ref.current?.setAttribute('aria-valuenow', String(Math.round(next)))
  }, [active])

  /** At most one write per frame, whatever the pointer's reporting rate. */
  const schedule = useCallback(
    (next: number) => {
      queued.current = next
      if (frame.current !== 0) return
      frame.current = requestAnimationFrame(() => {
        frame.current = 0
        /*
         * Nothing moved since the last write: skip it, and with it a layout.
         *
         * Compared HERE rather than in the move handler, which is the bug
         * this replaced: a move handler comparing against the applied value
         * would discard the last move of a drag that wandered away and came
         * back to where it started, and the stale queued value would then be
         * committed on release.
         */
        if (queued.current === live.current) return
        apply(queued.current)
      })
    },
    [apply],
  )

  // The committed value, and anything that changes it from outside.
  useLayoutEffect(() => {
    if (from.current === null) apply(pct)
  }, [pct, apply])

  useEffect(
    () => () => {
      if (frame.current !== 0) cancelAnimationFrame(frame.current)
    },
    [],
  )

  /*
   * The dragging class goes on the WORKSPACE: it suppresses the drawer's
   * width transition and kills text selection across both panes, and neither
   * is a descendant of the handle.
   */
  useEffect(() => {
    const ws = ref.current?.parentElement
    if (!ws) return
    ws.classList.toggle('dragging', dragging)
    return () => ws.classList.remove('dragging')
  }, [dragging])

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!active) return
    const ws = ref.current?.parentElement
    if (!ws) return
    const width = ws.getBoundingClientRect().width
    if (!(width > 0)) return
    from.current = { x: e.clientX, pct: live.current, width }
    e.currentTarget.setPointerCapture(e.pointerId)
    setDragging(true)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const start = from.current
    if (!start) return
    // Dragging LEFT grows the book, which is the side the handle belongs to.
    const moved = start.x - e.clientX
    schedule(clampPct(start.pct + (moved / start.width) * 100))
  }

  const end = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!from.current) return
    setDragging(false)
    if (frame.current !== 0) {
      cancelAnimationFrame(frame.current)
      frame.current = 0
      apply(queued.current)
    }
    from.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    // ONE state change per drag, on release, which is also the only place
    // the preference is persisted.
    onPct(live.current)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!active) return
    // Left grows the book, matching the drag direction.
    if (e.key === 'ArrowLeft') onPct(live.current + 2)
    else if (e.key === 'ArrowRight') onPct(live.current - 2)
    else if (e.key === 'Home') onPct(DRAWER_MIN_PCT)
    else if (e.key === 'End') onPct(DRAWER_MAX_PCT)
    else return
    e.preventDefault()
  }

  return (
    <div
      ref={ref}
      className={cn('tr-resizer', active && 'on', dragging && 'dragging')}
      // A separator only when it separates something adjustable; in rail
      // mode it is a 10px gap and announcing it would be noise.
      {...(active
        ? {
            role: 'separator' as const,
            'aria-orientation': 'vertical' as const,
            'aria-label': 'Resize the positions panel',
            'aria-valuenow': Math.round(pct),
            'aria-valuemin': DRAWER_MIN_PCT,
            'aria-valuemax': DRAWER_MAX_PCT,
            tabIndex: 0,
          }
        : { 'aria-hidden': true })}
      data-testid="drawer-resizer"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={onKeyDown}
      onDoubleClick={() => active && onPct(DRAWER_DEFAULT_PCT)}
      title={active ? 'Drag to resize · double-click for 50%' : undefined}
    >
      {active && (
        <span className="tr-grip">
          <GripVertical size={12} />
        </span>
      )}
    </div>
  )
}
